import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart3,
  Download,
  Eye,
  EyeOff,
  PanelLeftClose,
  PanelLeftOpen,
  RotateCcw,
  Search,
  Table as TableIcon,
  X,
} from 'lucide-react';
import { Button } from '@/components/UI/Button';
import { cn } from '@/lib/utils';
import type { ChartData } from '../../types/dashboard';
import { LineageBody } from './LineagePanel';
import {
  ChartRenderer,
  formatNumberFull,
  getDisplayName,
  getChartNumericFields,
  stripChartPrefix,
} from '../charts';
import { FinanceV2DataTable } from './FinanceV2DataTable';

// ``ExpandedTab`` removed — the inspector no longer uses tabs;
// it renders the unified LineageBody (same UX as the Info-icon
// LineagePanel) plus a modal-only SeriesStatsSection.
type ExpandedView = 'chart' | 'table';
type ChartTopNValue = number | 'all';
type ModalSortMode = 'none' | 'high_to_low' | 'low_to_high' | 'category_asc' | 'category_desc';

interface ChartExpandedModalProps {
  chart: ChartData;
  data: Record<string, unknown>[];
  originalRowCount?: number;
  appliedTopN?: ChartTopNValue;
  sortLabel?: string;
  colors?: string[];
  onClose: () => void;
}

const MODAL_TOP_N_OPTIONS = [5, 10, 20, 'all'] as const;
const MODAL_SIDE_MIN = 280;
const MODAL_SIDE_MAX = 560;
const MODAL_SIDE_DEFAULT = 360;
const CATEGORY_CANDIDATE_KEYS = ['name', 'label', 'category', 'date', 'x', 'dimension'];

const CHART_TYPE_LABELS: Partial<Record<ChartData['type'], string>> = {
  bar: 'Bar',
  horizontal_bar: 'Horizontal',
  multi_column_bar: 'Grouped',
  stacked_bar: 'Stacked',
  line: 'Line',
  area: 'Area',
  pie: 'Pie',
  donut: 'Donut',
  treemap: 'Treemap',
  waterfall: 'Waterfall',
};

interface SeriesSummary {
  field: string;
  label: string;
  count: number;
  sum: number;
  avg: number;
  min: number;
  max: number;
}

// Repo-2 branding pass: local modal tokens keep this styling scoped to Smart Insights.
const modalOutlineButtonClassName =
  "h-8 gap-1.5 rounded-[8px] border border-[#EDE6E1] bg-white px-2.5 text-xs font-medium text-[#7B6B72] shadow-none transition-colors hover:border-[#6E3450]/35 hover:bg-white hover:text-[#1F1A20]";
const modalIconButtonClassName =
  "h-8 w-8 rounded-[8px] p-0 text-[#7B6B72] shadow-none transition-colors hover:bg-[#FAF7F1] hover:text-[#1F1A20]";
const modalActiveButtonClassName =
  "h-8 gap-1.5 rounded-[8px] border border-[#D9CFC4] bg-[#f5e9ee] px-2.5 text-xs font-medium text-[#6E3450] shadow-none transition-colors hover:bg-[#f5e9ee]/80 hover:text-[#6E3450]";

function safeFileName(value: string): string {
  return value
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'chart';
}

function downloadTextFile(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function csvEscape(value: unknown): string {
  if (value == null) return '';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function buildCsv(rows: Record<string, unknown>[], columns: string[]): string {
  return [
    columns.map(csvEscape).join(','),
    ...rows.map((row) => columns.map((column) => csvEscape(row[column])).join(',')),
  ].join('\n');
}

function formatCell(value: unknown, format?: string): string {
  if (value == null || value === '') return '-';
  if (typeof value === 'number') return formatNumberFull(value, format);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatLabel(key: string): string {
  return stripChartPrefix(key).replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function toNumericValue(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.trim().replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function getCategoryValue(row: Record<string, unknown>): string {
  for (const key of CATEGORY_CANDIDATE_KEYS) {
    const value = row[key];
    if (value != null && value !== '') return String(value);
  }

  const fallbackKey = Object.keys(row).find((key) => !Number.isFinite(Number(String(row[key]).replace(/,/g, ''))));
  return fallbackKey ? String(row[fallbackKey] ?? '') : '';
}

function getPrimaryRowValue(row: Record<string, unknown>, fields: string[]): number {
  if (fields.length === 0) return toNumericValue(row.value);
  return fields.reduce((total, field) => total + toNumericValue(row[field] ?? row.value), 0);
}

function sortModalRows(rows: Record<string, unknown>[], fields: string[], mode: ModalSortMode): Record<string, unknown>[] {
  const sorted = [...rows];
  if (mode === 'none') return sorted;

  if (mode === 'category_asc' || mode === 'category_desc') {
    sorted.sort((a, b) => getCategoryValue(a).localeCompare(getCategoryValue(b)));
    return mode === 'category_desc' ? sorted.reverse() : sorted;
  }

  sorted.sort((a, b) => getPrimaryRowValue(a, fields) - getPrimaryRowValue(b, fields));
  return mode === 'high_to_low' ? sorted.reverse() : sorted;
}

function getSupportedChartTypes(chart: ChartData, numericFieldCount: number): ChartData['type'][] {
  const current = chart.type;
  const typeOptions: ChartData['type'][] = numericFieldCount > 1
    ? ['multi_column_bar', 'stacked_bar', 'line', 'area']
    : ['bar', 'horizontal_bar', 'line', 'area'];

  if (chart.type === 'pie' || chart.type === 'donut') typeOptions.push('pie', 'donut');
  if (chart.type === 'treemap') typeOptions.push('treemap');
  if (chart.type === 'waterfall') typeOptions.push('waterfall');

  return Array.from(new Set([current, ...typeOptions]));
}

function formatTopNLabel(value: ChartTopNValue): string {
  return value === 'all' ? 'All' : `Top ${value}`;
}

function getChartTypeLabel(chart: ChartData): string {
  // Type label sources: explicit ``template.chartType`` if a template
  // override is active, otherwise the BE-stamped ``chart.type``. The
  // legacy ``drillDown.chartMetadata.chartType`` fallback is gone.
  return chart.template?.chartType?.toString() || chart.type;
}

function getChartSubtitle(chart: ChartData): string {
  // Subtitle sources: explicit template subtitle; lineage_v2's
  // "what_it_is" CFO-grade description; chart reasoning. The legacy
  // calculationInfo.description / drillDown.description fallbacks
  // are gone.
  return chart.template?.subtitle
    || chart.lineage_v2?.what_it_is
    || chart.reasoning
    || '';
}

function getAllColumns(rows: Record<string, unknown>[]): string[] {
  return Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
}

function getSeriesSummaries(chart: ChartData, rows: Record<string, unknown>[]): SeriesSummary[] {
  const fields = getChartNumericFields(chart, rows);
  return fields.map((field) => {
    const values = rows
      .map((row) => Number(row[field] ?? row.value ?? 0))
      .filter((value) => Number.isFinite(value));
    const sum = values.reduce((total, value) => total + value, 0);
    const count = values.length;
    return {
      field,
      label: getDisplayName(chart, field),
      count,
      sum,
      avg: count ? sum / count : 0,
      min: count ? Math.min(...values) : 0,
      max: count ? Math.max(...values) : 0,
    };
  });
}

function getTableSummaryRows(columns: string[], summaries: SeriesSummary[]): Array<{ label: string; values: Record<string, string> }> {
  if (columns.length <= 1 || summaries.length === 0) return [];
  const byField = new Map(summaries.map((summary) => [summary.field, summary]));
  const summaryKeys: Array<{ label: string; key: keyof Pick<SeriesSummary, 'sum' | 'avg' | 'min' | 'max'> }> = [
    { label: 'Sum', key: 'sum' },
    { label: 'Avg', key: 'avg' },
    { label: 'Min', key: 'min' },
    { label: 'Max', key: 'max' },
  ];

  return summaryKeys.map(({ label, key }) => ({
    label,
    values: Object.fromEntries(
      columns.slice(1).map((column) => {
        const summary = byField.get(column);
        return [column, summary ? formatNumberFull(summary[key]) : '-'];
      })
    ),
  }));
}

function inferChartMinWidth(chart: ChartData, rowCount: number): number {
  const denseTypes = new Set(['bar', 'line', 'area', 'multi_column_bar', 'stacked_bar', 'dual_axis_line', 'histogram', 'waterfall']);
  const wideTypes = new Set(['horizontal_bar', 'diverging_bar', 'candlestick', 'parallel_coordinates']);
  const safeRows = Math.max(1, rowCount);

  if (wideTypes.has(chart.type)) return Math.max(980, Math.min(2600, safeRows * 74));
  if (denseTypes.has(chart.type)) return Math.max(980, Math.min(2600, safeRows * (safeRows <= 6 ? 110 : 70)));
  return 980;
}

// ``FactRow`` and ``DetailSection`` removed — the inspector now uses
// the unified LineageBody chrome (SectionHeader / LineageCard) from
// LineagePanel. Single source of truth for "lineage as a panel".

// Inspector — uses the unified LineageBody (WHAT THIS IS title /
// Dimensions / Values / OData References) — same as the Info-icon
// LineagePanel. The earlier modal-only "Data" stats section was
// removed; it didn't add information beyond what the chart canvas
// already shows, and it broke the parity with the side-rail panel.
function MetadataPanel({
  chart,
}: {
  chart: ChartData;
  rows?: Record<string, unknown>[];
  originalRowCount?: number;
  summaries?: SeriesSummary[];
}) {
  return (
    <aside className="min-h-full border-r border-[#EDE6E1] bg-white">
      {/* No "Inspector" mini-header — the LineageBody renders its own
          ``Data Lineage`` title above the chart name, so a separate
          inspector label was redundant chrome. */}
      <div className="px-5 py-5">
        <LineageBody
          title={chart.title || 'Chart details'}
          subtitle={chart.caption || ''}
          lineage={chart.lineage_v2}
        />
      </div>
    </aside>
  );
}

export function ChartExpandedModal({
  chart,
  data,
  originalRowCount,
  appliedTopN,
  sortLabel,
  colors,
  onClose,
}: ChartExpandedModalProps) {
  const [view, setView] = useState<ExpandedView>('chart');
  const [sideOpen, setSideOpen] = useState(true);
  const [sideWidth, setSideWidth] = useState(MODAL_SIDE_DEFAULT);
  const [showLabels, setShowLabels] = useState(false);
  // Removed legacy tab state — inspector renders one scrollable column.
  const [sortMode, setSortMode] = useState<ModalSortMode>('none');
  const [topN, setTopN] = useState<ChartTopNValue>(appliedTopN ?? 'all');
  const [search, setSearch] = useState('');
  const [chartType, setChartType] = useState<ChartData['type']>(chart.type);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isInput = !!target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName);
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (isInput) return;
      if (event.key.toLowerCase() === 't') {
        event.preventDefault();
        setView((current) => current === 'chart' ? 'table' : 'chart');
      }
      if (event.key.toLowerCase() === 's') {
        event.preventDefault();
        setSideOpen((current) => !current);
      }
      if (event.key.toLowerCase() === 'l') {
        event.preventDefault();
        setShowLabels((current) => !current);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const startSideResize = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragRef.current = { startX: event.clientX, startWidth: sideWidth };
    document.body.style.cursor = 'col-resize';

    const handleMove = (moveEvent: MouseEvent) => {
      const dragState = dragRef.current;
      if (!dragState) return;
      const nextWidth = dragState.startWidth + (moveEvent.clientX - dragState.startX);
      setSideWidth(Math.min(MODAL_SIDE_MAX, Math.max(MODAL_SIDE_MIN, nextWidth)));
    };

    const handleUp = () => {
      dragRef.current = null;
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const sourceRows = useMemo(() => {
    const fullData = chart.fullData && chart.fullData.length > 0 ? chart.fullData : undefined;
    if (fullData) return fullData as Record<string, unknown>[];
    if (data.length > 0) return data as Record<string, unknown>[];
    return (chart.data || []) as Record<string, unknown>[];
  }, [chart.data, chart.fullData, data]);
  const sourceNumericFields = useMemo(() => getChartNumericFields(chart, sourceRows), [chart, sourceRows]);
  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return sourceRows;
    return sourceRows.filter((row) => getCategoryValue(row).toLowerCase().includes(needle));
  }, [search, sourceRows]);
  const sortedRows = useMemo(() => sortModalRows(filteredRows, sourceNumericFields, sortMode), [filteredRows, sourceNumericFields, sortMode]);
  const normalizedRows = useMemo(() => (
    topN === 'all' ? sortedRows : sortedRows.slice(0, topN)
  ), [sortedRows, topN]);
  const columns = useMemo(() => getAllColumns(normalizedRows), [normalizedRows]);
  const summaries = useMemo(() => getSeriesSummaries(chart, normalizedRows), [chart, normalizedRows]);
  const tableSummaryRows = useMemo(() => getTableSummaryRows(columns, summaries), [columns, summaries]);
  const chartTypeOptions = useMemo(() => getSupportedChartTypes(chart, sourceNumericFields.length), [chart, sourceNumericFields.length]);
  const modalTopNOptions = useMemo(() => {
    const numericOptions = MODAL_TOP_N_OPTIONS.reduce<number[]>((options, option) => (
      typeof option === 'number' ? [...options, option] : options
    ), []);
    const values = typeof topN === 'number' && !numericOptions.includes(topN)
      ? [...numericOptions, topN].sort((a, b) => a - b)
      : numericOptions;
    return [...values, 'all'] as ChartTopNValue[];
  }, [topN]);
  const hasDirtyToolbarState = sortMode !== 'none' || topN !== 'all' || search.trim().length > 0 || chartType !== chart.type;
  const chartSubtitle = getChartSubtitle(chart);
  const totalRows = originalRowCount ?? sourceRows.length;
  const chartToRender: ChartData = useMemo(() => ({
    ...chart,
    type: chartType,
    data: normalizedRows,
    template: chart.template ? { ...chart.template, chartType, data: normalizedRows } : undefined,
  }), [chart, chartType, normalizedRows]);
  const chartMinWidth = inferChartMinWidth(chart, normalizedRows.length);

  const handleCsvExport = () => {
    if (normalizedRows.length === 0 || columns.length === 0) return;
    downloadTextFile(
      `${safeFileName(chart.title || 'chart')}.csv`,
      buildCsv(normalizedRows, columns),
      'text/csv;charset=utf-8'
    );
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-5">
      <button
        type="button"
        className="absolute inset-0 bg-neutral-950/45 backdrop-blur-[2px]"
        aria-label="Close expanded chart"
        onClick={onClose}
      />

      <div className="relative flex h-[92vh] w-[min(96vw,1600px)] flex-col overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white shadow-[0_24px_70px_-28px_rgba(31,26,32,.55)]">
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-[#EDE6E1] bg-white px-5 py-4">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2 font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E3450]">
              Insight canvas
              <span className="text-[#7B6B72]">· {getChartTypeLabel(chart)}</span>
            </div>
            <h2 className="truncate font-display text-[22px] font-semibold leading-tight text-[#1F1A20]" title={chart.title}>
              {chart.title || 'Chart'}
            </h2>
            {chartSubtitle && (
              <p className="mt-2 line-clamp-1 max-w-5xl text-[13px] italic text-[#7B6B72]" title={chartSubtitle}>
                {chartSubtitle}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div className="hidden items-center gap-1 text-[10.5px] text-[#7B6B72] lg:flex">
              <kbd className="rounded-[6px] border border-[#EDE6E1] bg-[#FAF7F1] px-1.5 py-0.5 font-mono text-[#3F3338]">Esc</kbd> close
              <span className="text-[#A8989F]">·</span>
              <kbd className="rounded-[6px] border border-[#EDE6E1] bg-[#FAF7F1] px-1.5 py-0.5 font-mono text-[#3F3338]">T</kbd> toggle table
              <span className="text-[#A8989F]">·</span>
              <kbd className="rounded-[6px] border border-[#EDE6E1] bg-[#FAF7F1] px-1.5 py-0.5 font-mono text-[#3F3338]">S</kbd> side panel
              <span className="text-[#A8989F]">·</span>
              <kbd className="rounded-[6px] border border-[#EDE6E1] bg-[#FAF7F1] px-1.5 py-0.5 font-mono text-[#3F3338]">L</kbd> labels
            </div>
            <Button variant="outline" size="sm" className={modalOutlineButtonClassName} onClick={() => setSideOpen((current) => !current)}>
              {sideOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
              {sideOpen ? 'Hide panel' : 'Show panel'}
            </Button>
            <Button variant="ghost" size="sm" className={modalIconButtonClassName} onClick={onClose} title="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="shrink-0 border-b border-[#EDE6E1] bg-[#FAF7F1] px-5 py-2">
          <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-[#EDE6E1] bg-[#FAF7F1]/70 px-2.5 py-2">
          <div className="inline-flex shrink-0 overflow-hidden rounded-[10px] border border-[#EDE6E1] bg-white p-[2px]">
            <button
              type="button"
              onClick={() => setView('chart')}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-xs font-medium transition-colors',
                view === 'chart' ? 'bg-[#6E3450] text-white' : 'text-[#7B6B72] hover:text-[#1F1A20]'
              )}
            >
              <BarChart3 className="h-3.5 w-3.5" />
              Chart
            </button>
            <button
              type="button"
              onClick={() => setView('table')}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-xs font-medium transition-colors',
                view === 'table' ? 'bg-[#6E3450] text-white' : 'text-[#7B6B72] hover:text-[#1F1A20]'
              )}
            >
              <TableIcon className="h-3.5 w-3.5" />
              Table
            </button>
          </div>

          <label className="inline-flex h-8 shrink-0 items-center gap-2">
            <span className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">Sort</span>
            <select
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value as ModalSortMode)}
              className="h-8 rounded-[8px] border border-[#EDE6E1] bg-white px-3 text-[12px] text-[#1F1A20] outline-none transition-colors hover:border-[#6E3450]/35 focus:border-[#6E3450]"
            >
              <option value="none">Default order</option>
              <option value="high_to_low">Value · high to low</option>
              <option value="low_to_high">Value · low to high</option>
              <option value="category_asc">Category · A to Z</option>
              <option value="category_desc">Category · Z to A</option>
            </select>
          </label>

          <div className="inline-flex h-8 shrink-0 items-center overflow-hidden rounded-[8px] border border-[#EDE6E1] bg-white p-[2px]">
            {modalTopNOptions.map((option) => {
              const active = topN === option;
              return (
                <button
                  key={String(option)}
                  type="button"
                  onClick={() => setTopN(option)}
                  className={cn(
                    "h-full rounded-[6px] px-2.5 text-[11px] font-medium transition-colors",
                    active ? "bg-[#4E7C89] text-white" : "text-[#7B6B72] hover:text-[#1F1A20]"
                  )}
                  title={option === 'all' ? 'Show all rows' : `Show top ${option} rows`}
                >
                  {formatTopNLabel(option)}
                </button>
              );
            })}
          </div>

          <label className="inline-flex h-8 min-w-[180px] shrink-0 items-center gap-2 rounded-[8px] border border-[#EDE6E1] bg-white px-2 transition-colors focus-within:border-[#6E3450]">
            <Search className="h-3.5 w-3.5 text-[#7B6B72]" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter categories..."
              className="min-w-0 flex-1 bg-transparent text-[12px] text-[#1F1A20] outline-none placeholder:text-[#A8989F]"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-[#7B6B72] transition-colors hover:text-[#1F1A20]"
                title="Clear filter"
              >
                ×
              </button>
            )}
          </label>

          <label className="inline-flex h-8 shrink-0 items-center gap-2">
            <span className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7B6B72]">Type</span>
            <select
              value={chartType}
              onChange={(event) => setChartType(event.target.value as ChartData['type'])}
              className="h-8 rounded-[8px] border border-[#EDE6E1] bg-white px-3 text-[12px] text-[#1F1A20] outline-none transition-colors hover:border-[#6E3450]/35 focus:border-[#6E3450]"
            >
              {chartTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {CHART_TYPE_LABELS[type] ?? formatLabel(type)}
                </option>
              ))}
            </select>
          </label>

          <Button
            variant={showLabels ? 'secondary' : 'outline'}
            size="sm"
            className={showLabels ? modalActiveButtonClassName : modalOutlineButtonClassName}
            onClick={() => setShowLabels((current) => !current)}
          >
            {showLabels ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            Labels
          </Button>

          <div className="ml-auto flex items-center gap-3 text-xs text-[#7B6B72]">
            <span>
              Showing <strong className="text-[#1F1A20]">{normalizedRows.length}</strong>
              {filteredRows.length !== normalizedRows.length ? ` of ${filteredRows.length}` : ''} rows
            </span>
            {filteredRows.length !== totalRows && <span>{totalRows} total</span>}
            {sortMode === 'none' && sortLabel && <span>{sortLabel}</span>}
            {hasDirtyToolbarState && (
              <Button
                variant="outline"
                size="sm"
                className={modalOutlineButtonClassName}
                onClick={() => {
                  setSortMode('none');
                  setTopN('all');
                  setSearch('');
                  setChartType(chart.type);
                }}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </Button>
            )}
            <Button variant="outline" size="sm" className={modalOutlineButtonClassName} onClick={handleCsvExport} disabled={normalizedRows.length === 0}>
              <Download className="h-3.5 w-3.5" />
              CSV
            </Button>
          </div>
          </div>
        </div>

        <div className="min-h-0 flex flex-1 overflow-y-auto scroll-thin">
          {sideOpen && (
            <>
              <div className="hidden shrink-0 lg:block" style={{ width: `${sideWidth}px` }}>
                <MetadataPanel
                  chart={chart}
                  rows={normalizedRows}
                  originalRowCount={totalRows}
                  summaries={summaries}
                />
              </div>
              <div
                className="hidden w-[5px] shrink-0 cursor-col-resize bg-[#EDE6E1] transition-colors hover:bg-[#6E3450]/30 lg:block"
                title="Drag to resize"
                onMouseDown={startSideResize}
              />
            </>
          )}

          <main className="min-w-0 flex-1 self-start bg-[#FAF7F1]/45">
            {view === 'chart' ? (
              <div className="overflow-x-auto overflow-y-hidden p-4 scroll-thin">
                <div
                  className="min-h-[520px] rounded-[10px] border border-[#EDE6E1] bg-white p-3"
                  style={{ minWidth: `${chartMinWidth}px` }}
                >
                  <ChartRenderer
                    chart={chartToRender}
                    height={Math.max(520, window.innerHeight - 260)}
                    colors={colors}
                    presentation={{
                      enableRangeSlider: true,
                      showValueLabels: showLabels,
                      useExternalScroll: true,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="p-4">
                {/* Repo-2 branding pass: reuse the v2 finance table surface inside expanded chart details. */}
                <FinanceV2DataTable
                  rows={normalizedRows}
                  columns={columns}
                  summaryRows={tableSummaryRows}
                  scrollAxes="horizontal"
                  getColumnLabel={(column) => chart.metric_display_names?.[column] ?? formatLabel(column)}
                  formatCell={(value, column) => formatCell(value, chart.metric_formats?.[column])}
                />
              </div>
            )}
          </main>
        </div>
      </div>
    </div>,
    document.body
  );
}
